import { Check, Play, ExternalLink, GitBranch } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const stappen = [
  { done: true, datum: "Juli 2025", title: "Accommodatiebeleid vastgesteld", text: "Gemeente Waadhoeke stelde een nieuw accommodatiebeleid vast met kaders voor renovatie en nieuwbouw." },
  { done: true, datum: "Eind 2025", title: "Werkgroep en inventarisatie gestart", text: "Een werkgroep vanuit het dorp is actief. Gesprekken met verenigingen zijn gestart." },
  { done: true, datum: "Voorjaar 2026", title: "Doarpscafé & enquête onder inwoners", text: "Op 16 april vond het Doarpscafé plaats. Aansluitend vulden 208 inwoners/huishoudens de enquête in — dat is 9% van alle volwassen inwoners en 17% van de huishoudens." },
  { done: true, datum: "Augustus 2026", title: "Uitslag enquête gedeeld", text: "Op 1 augustus is de uitslag van de enquête gedeeld met het dorp. De belangrijkste inzichten en het volledige rapport zijn te lezen op de pagina 'Uitslag enquête'." },
  { done: true, datum: "September 2026", title: "Gesprekken tussen werkgroep en verenigingen", text: "Verenigingen zijn per mail uitgenodigd voor gesprekken met de werkgroep over de vervolgstappen. De rode draden zijn gebundeld in een verslag.", link: { href: "/downloads/verslag-gesprekken-verenigingen.pdf", label: "Lees het verslag" } },
  { actief: true, datum: "24 september 2026", title: "Startnotitie nieuwbouw school", text: "De gemeente Waadhoeke agendeert de startnotitie voor de nieuwbouw van de school in Menaam. Bekijk de vergaderstukken bij de gemeenteraad.", badge: "Nu", link: { href: "https://waadhoeke.bestuurlijkeinformatie.nl/Agenda/Index/496a2d53-b6f6-4130-b414-536d20733527", label: "Bekijk de agenda" } },
  { datum: "Begin 2027", title: "Besluit locatie door gemeenteraad", text: "Gepland moment waarop de raad een besluit neemt over de nieuwe school.", badge: "Nog niet zeker", open: true },
  { datum: "Daarna", title: "Ontwerp, subsidies, exploitatie, financiering en bouw", text: "Pas ná de locatiekeuze kunnen het programma van eisen, de exploitatie, de financiering, subsidieaanvragen en bouwplannen worden uitgewerkt.", badge: "Onbekend", open: true },
];

export default function Planning() {
  return (
    <section id="planning" data-testid="planning-section" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow testid="planning-eyebrow">Proces</Eyebrow>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A2E22] mb-16">
            Hoe gaat het verder?
          </h2>
        </Reveal>

        <div className="relative max-w-3xl pl-2">
          {/* vertical line */}
          <div className="absolute top-2 bottom-2 left-[19px] w-0.5 bg-[#74C69D]/40" />

          {stappen.map((s, i) => {
            const dotBg = s.done ? "bg-[#2D6A4F]" : s.actief ? "bg-[#F4A261]" : "bg-[#FAF7F2] border-2 border-[#2D6A4F]/25";
            const dotText = s.done || s.actief ? "text-white" : "text-[#2D6A4F]";
            return (
              <Reveal key={i} delay={i * 0.08}>
                <div className="flex gap-6 pb-10 last:pb-0">
                  <div className={`relative z-10 shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${dotBg} ${dotText}`}>
                    {s.done ? <Check size={18} /> : s.actief ? <Play size={15} className="fill-current" /> : i + 1}
                  </div>
                  <div
                    data-testid={`planning-step-${i}`}
                    className={`flex-1 rounded-2xl border p-6 transition-all ${
                      s.actief
                        ? "border-[#F4A261]/50 bg-[#F4A261]/5 shadow-sm"
                        : "border-[#2D6A4F]/15 bg-[#FAF7F2]"
                    } ${s.open ? "opacity-70" : ""}`}
                  >
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      <span className="text-xs font-bold tracking-[0.08em] uppercase text-[#2D6A4F]">
                        {s.datum}
                      </span>
                      {s.badge && (
                        <span
                          className={`text-[0.68rem] font-bold px-2.5 py-0.5 rounded-full ${
                            s.actief ? "bg-[#F4A261] text-[#1A2E22]" : "bg-[#6B7C6E]/15 text-[#6B7C6E]"
                          }`}
                        >
                          {s.badge}
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading text-lg font-bold text-[#1A2E22] mb-1">{s.title}</h3>
                    <p className="text-sm leading-relaxed text-[#1A2E22]/65 m-0">{s.text}</p>
                    {s.link && (
                      <a
                        href={s.link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`planning-step-${i}-link`}
                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#2D6A4F] hover:text-[#245a41] transition-colors"
                      >
                        {s.link.label} <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-10 max-w-3xl">
            <a
              href="/procesboom.html"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="planning-procesboom-link"
              className="group flex items-center gap-4 rounded-2xl border border-[#2D6A4F]/15 bg-[#FAF7F2] px-6 py-5 hover:border-[#2D6A4F]/35 hover:shadow-sm transition-all"
            >
              <span className="shrink-0 w-11 h-11 rounded-xl bg-[#2D6A4F]/10 text-[#2D6A4F] flex items-center justify-center">
                <GitBranch size={20} />
              </span>
              <span className="flex-1">
                <span className="block font-heading font-bold text-[#1A2E22]">
                  Bekijk de volledige beslisboom
                  <span className="ml-2 text-[0.68rem] font-bold uppercase tracking-[0.1em] text-[#F4A261]">
                    Concept
                  </span>
                </span>
                <span className="block text-sm text-[#1A2E22]/65">
                  Alle stappen, keuzes en beslismomenten schematisch op één pagina. Nog niet definitief.
                </span>
              </span>
              <ExternalLink
                size={18}
                className="text-[#2D6A4F] group-hover:translate-x-0.5 transition-transform shrink-0"
              />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
